import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../LanguageContext';
import { Car, Mail, MapPin, Phone, ArrowRight, Globe, Shield, Zap, Users, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const Footer = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  const stats = [
    { value: '12K+', label: t('footer.carsListed'), icon: Car },
    { value: '50K+', label: t('footer.happyUsers'), icon: Users },
    { value: '120+', label: t('footer.vinDbs'), icon: Shield },
    { value: '30+', label: t('footer.countries'), icon: Globe },
  ];

  return (
    <footer className="bg-[#030508] border-t border-white/5 relative z-10">
      {/* Newsletter Banner */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-xl font-bold mb-1">
                {t('footer.newsletter')} <span className="text-cyan-500 neon-text">{t('footer.fast')}</span>
              </h3>
              <p className="text-sm text-gray-400">{t('footer.newsDesc')}</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-72">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.emailPlaceholder')}
                  className="w-full h-10 pl-10 pr-4 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500/50 outline-none"
                  required
                />
              </div>
              <Button type="submit" className="h-10 px-5 bg-cyan-500 text-black hover:bg-cyan-400 font-semibold text-sm shadow-neon shrink-0">
                {subscribed ? t('footer.subscribed') : t('footer.subscribe')}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                  <stat.icon className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-gray-500">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img src="/logo-nav.webp" alt="CarHipo" className="h-8 w-auto" />
            </Link>
            <p className="text-gray-500 text-xs leading-relaxed mb-4">
              AI-powered car marketplace. Smart recommendations, verified VIN checks, comprehensive wiki.
            </p>
            <div className="flex items-center gap-2">
              <a href="#" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 border border-transparent hover:border-cyan-500/30 transition-all">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 border border-transparent hover:border-cyan-500/30 transition-all">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 border border-transparent hover:border-cyan-500/30 transition-all">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center hover:bg-cyan-500/20 border border-transparent hover:border-cyan-500/30 transition-all">
                <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              </a>
            </div>
          </div>

          {/* Marketplace */}
          <div>
            <h4 className="font-heading text-xs font-semibold text-white uppercase tracking-wider mb-4">{t('footer.marketplace')}</h4>
            <ul className="space-y-2.5">
              <li><Link to="/cars" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />{t('footer.browseCars')}</Link></li>
              <li><Link to="/sell" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />{t('footer.sellYourCar')}</Link></li>
              <li><Link to="/cars?condition=new" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />{t('footer.newCars')}</Link></li>
              <li><Link to="/cars?condition=used" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />{t('footer.usedCars')}</Link></li>
              <li><Link to="/cars?body_type=SUV" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />{t('footer.suvs')}</Link></li>
              <li><Link to="/cars?fuel_type=Electric" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />{t('footer.electric')}</Link></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-heading text-xs font-semibold text-white uppercase tracking-wider mb-4">Resources</h4>
            <ul className="space-y-2.5">
              <li><Link to="/wiki" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Car Wiki</Link></li>
              <li><Link to="/vin-check" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />VIN Check</Link></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Buying Guide</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Selling Tips</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Market Reports</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Car Reviews</a></li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading text-xs font-semibold text-white uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-2.5">
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />About Us</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Careers</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Press</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Blog</a></li>
              <li><a href="#" className="text-gray-500 text-xs hover:text-cyan-500 transition-colors flex items-center gap-1"><ChevronRight className="w-3 h-3" />Partners</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-xs font-semibold text-white uppercase tracking-wider mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-gray-500 text-xs">
                <Mail className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                support@carhippo.com
              </li>
              <li className="flex items-center gap-2 text-gray-500 text-xs">
                <Phone className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                +1 (555) 123-4567
              </li>
              <li className="flex items-center gap-2 text-gray-500 text-xs">
                <MapPin className="w-3.5 h-3.5 text-cyan-500 shrink-0" />
                San Francisco, CA
              </li>
            </ul>
            <div className="mt-4 p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
              <div className="flex items-center gap-2 mb-1">
                <Zap className="w-3.5 h-3.5 text-cyan-500" />
                <span className="text-xs font-semibold text-white">AI Support</span>
              </div>
              <p className="text-[10px] text-gray-500">24/7 AI assistant available via live chat</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <a href="#" className="text-gray-600 text-xs hover:text-cyan-500 transition-colors">Privacy</a>
              <span className="text-gray-800">&middot;</span>
              <a href="#" className="text-gray-600 text-xs hover:text-cyan-500 transition-colors">Terms</a>
              <span className="text-gray-800">&middot;</span>
              <a href="#" className="text-gray-600 text-xs hover:text-cyan-500 transition-colors">Cookies</a>
              <span className="text-gray-800">&middot;</span>
              <a href="#" className="text-gray-600 text-xs hover:text-cyan-500 transition-colors">GDPR</a>
            </div>
            <p className="text-gray-600 text-xs">
              &copy; {new Date().getFullYear()} CarHippo. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
